/**
 * A second read of selected pens on a fixed bucket grid, alongside the normal
 * pen fetch, which cannot serve this: it picks its interval from the window and
 * pen count and may serve some pens raw.
 *
 * Fetched rather than averaged client-side because `toStartOfInterval` snaps
 * buckets to an absolute grid, while a local average would anchor to whatever
 * the window starts at and match training's bucket edges only by luck.
 */
import { ChartDataPoint, TimeRange } from 'features/systemLens'
import {
  AggregationOp,
  isAbortError,
  TimeSeriesRequest,
  TimeSeriesResponse,
  useTimeSeriesService,
} from 'features/timeSeries/timeSeriesService'
import { formatLocalChartTime } from 'features/timeSeries/utils/mergeTimeSeriesResponse'
import { bucketColumnId } from 'features/timeSeries/utils/penCompanions'
import { useEffect, useRef, useState } from 'react'
import { ResampleAggregation, ResampleConfig } from 'types'

/** Bucket values per pen: sensor id -> bucket start (epoch ms) -> value. */
export type PenBucketSeries = Map<string, Map<number, number>>

const AGGREGATION_OPS: Record<ResampleAggregation, AggregationOp> = {
  avg: AggregationOp.MEAN,
  min: AggregationOp.MIN,
  max: AggregationOp.MAX,
  sum: AggregationOp.SUM,
}

interface BucketGrid {
  interval: number
  interval_unit: TimeSeriesRequest['interval_unit']
  aggregation_op: AggregationOp
  sensorIds: string[]
}

/** One request per distinct grid, so pens sharing an interval and op share one. */
export const groupBucketRequests = (
  configs: ResampleConfig[]
): BucketGrid[] => {
  const groups = new Map<string, BucketGrid>()

  configs.forEach((config) => {
    const key = `${config.interval}|${config.intervalUnit}|${config.aggregationOp}`
    const group = groups.get(key)
    if (group) {
      group.sensorIds.push(config.sensorId)
      return
    }
    groups.set(key, {
      interval: config.interval,
      interval_unit: config.intervalUnit,
      aggregation_op: AGGREGATION_OPS[config.aggregationOp],
      sensorIds: [config.sensorId],
    })
  })

  return Array.from(groups.values())
}

const collectBucketSeries = (
  response: TimeSeriesResponse,
  sensorIds: string[],
  into: PenBucketSeries
): void => {
  sensorIds.forEach((sensorId) => {
    const points = response[sensorId]
    if (!points) return

    const series = into.get(sensorId) ?? new Map<number, number>()
    points.forEach((point) => {
      if (point.value === null) return
      series.set(Date.parse(point.timestamp), point.value)
    })
    if (series.size > 0) into.set(sensorId, series)
  })
}

/**
 * Identity of a fetch. The grid belongs in here as well as the window, or
 * changing the interval would leave the old buckets on screen.
 */
export const bucketFetchKey = (
  configs: ResampleConfig[],
  timeRange: TimeRange
): string =>
  JSON.stringify({
    from: timeRange.from,
    to: timeRange.to,
    grids: configs
      .map(
        (config) =>
          `${config.sensorId}|${config.interval}|${config.intervalUnit}|${config.aggregationOp}`
      )
      .sort(),
  })

const EMPTY_BUCKETS: PenBucketSeries = new Map()

export const usePenBuckets = (
  resampleConfigs: Record<string, ResampleConfig>,
  timeRange: TimeRange
): PenBucketSeries => {
  const { fetchBucketedData } = useTimeSeriesService()
  const [buckets, setBuckets] = useState<PenBucketSeries>(EMPTY_BUCKETS)

  const enabled = Object.values(resampleConfigs).filter(
    (config) => config.enabled
  )
  const key = enabled.length > 0 ? bucketFetchKey(enabled, timeRange) : ''

  // `key` fully describes the request, so the details behind it live in a ref
  // and the effect depends on the key alone
  const requestRef = useRef({ configs: enabled, timeRange })
  requestRef.current = { configs: enabled, timeRange }

  const latestKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (key === '') {
      latestKeyRef.current = null
      setBuckets((previous) => (previous.size === 0 ? previous : EMPTY_BUCKETS))
      return
    }

    const controller = new AbortController()
    const { configs, timeRange: range } = requestRef.current
    latestKeyRef.current = key

    const run = async () => {
      const collected: PenBucketSeries = new Map()

      try {
        await Promise.all(
          groupBucketRequests(configs).map(async (grid) => {
            const response = await fetchBucketedData(
              {
                entry_ids: grid.sensorIds,
                start_datetime: range.from,
                end_datetime: range.to,
                interval: grid.interval,
                interval_unit: grid.interval_unit,
                aggregation_op: grid.aggregation_op,
              },
              { signal: controller.signal }
            )
            collectBucketSeries(response, grid.sensorIds, collected)
          })
        )
      } catch (error) {
        if (isAbortError(error)) return
        // The pens come from a different request, so this loses the overlay only
        console.error('Failed to fetch training buckets:', error)
        return
      }

      // a newer view is in flight; its result is the one to paint
      if (latestKeyRef.current !== key) return
      setBuckets(collected)
    }

    void run()

    return () => controller.abort()
  }, [key, fetchBucketedData])

  return buckets
}

/**
 * Bucket values as extra columns on the chart rows, adding rows where a bucket
 * start has no pen timestamp to sit on. Added rows read as a gap for every pen,
 * which their `spanGaps` covers.
 *
 * Copies any row it writes into, since these can be the same objects held in
 * `rawChartData` when no earlier transform applied.
 */
export const mergeBucketColumns = (
  rows: ChartDataPoint[],
  buckets: PenBucketSeries
): ChartDataPoint[] => {
  if (buckets.size === 0) return rows

  const byTimestamp = new Map<number, ChartDataPoint>()
  rows.forEach((row) => byTimestamp.set(row.timestamp, row))

  const touched = new Set<number>()
  let addedRow = false

  buckets.forEach((series, sensorId) => {
    const columnId = bucketColumnId(sensorId)
    series.forEach((value, timestamp) => {
      let row = byTimestamp.get(timestamp)
      if (row === undefined) {
        row = { timestamp, time: formatLocalChartTime(timestamp) }
        byTimestamp.set(timestamp, row)
        addedRow = true
      } else if (!touched.has(timestamp)) {
        row = { ...row }
        byTimestamp.set(timestamp, row)
      }
      touched.add(timestamp)
      row[columnId] = value
    })
  })

  const merged = Array.from(byTimestamp.values())
  // only new rows can break the ordering
  return addedRow
    ? merged.sort((left, right) => left.timestamp - right.timestamp)
    : merged
}
